import { BulkPayment, BulkTypes } from '../models/BulkPayment';
import { Pleb } from '../models/Pleb';
import { apiResponseToTransaction, PaymentMethod, PaymentType, Transaction } from '../models/Transactions';
import { FakeData } from './FakeData';
import { Repository } from './Repository';

export interface LockedFunds {
  until: string;
  amountMsat: number;
}

export interface Wallet {
  balance: number;
  lnurl: string;
  lockedFunds?: LockedFunds;
  nwcConnectionString?: string;
}

export interface ScanData {
  domain?: string;
  callback?: string;
  maxSendable?: number;
  minSendable?: number;
  description_hash?: string;
  description?: string;
  lnurl?: string;
  commentAllowed: number;
  opaFeesInPercent?: number;
  invoice?: string;
  fixedAmountMsat?: number;
  fixedComment?: number;
  lockedFunds?: LockedFunds;
  walletInternal?: object;
}

export interface LatestRecipient {
  type: string;
  lnurl: string;
  latestPaymentTime: string;
  internalUser: Pleb;
}

export enum TypeSearch {
  user = 'user',
  bulk_special = 'bulk_special',
  city = 'city',
  distanceFromMyself = 'distanceFromMyself',
}

export interface SearchResponse {
  type: TypeSearch;
  user?: Pleb;
  bulkSpecial?: BulkPayment;
  city?: City;
  distanceFromMyself?: DistanceFromMyself;
  imgUrl?: string;
}

export interface City {
  id: string;
  name: string;
  mainText: string;
  secondaryText: string;
  geoZap?: {
    addressType?: string;
    countryCode?: string;
    countryImgUrl?: string;
  }
}

export interface DistanceFromMyself {
  id: string;
  name: string;
}
export class WalletRepository extends Repository {
  async getWallet(): Promise<Wallet> {
    if(Repository.offlineMode)
    {
      return FakeData.getWallet();
    }
    
    const path = `/v1/wallet-internal`;
    const response = await Repository.client.get(path);
    return {
      balance: response.data.data.balance,
      lnurl: response.data.data.lnurl,
      lockedFunds: response.data.data?.lockedFunds ?? undefined,
      nwcConnectionString: response.data.data?.nwcConnectionString ?? undefined
    };
  }

  async getTransactions(limit: number = 10, page: number = 0): Promise<Transaction[]> {
    if(Repository.offlineMode)
    {
      return [FakeData.getTransaction()];
    }

    const path = `/v1/wallet-internal/payments?limit=${limit}&page=${page}&paymentTypes=${PaymentType.individual}&paymentTypes=${PaymentType.bulk}&paymentMethods=${PaymentMethod.LNURL}&paymentMethods=${PaymentMethod.INVOICE}`;
    const response = await Repository.client.get(path);
    return response.data.data.map((element: any) => {
      return apiResponseToTransaction(element);
    }) as Transaction[];
  }

  async getTransaction(paymentHash: string): Promise<Transaction> {
    if(Repository.offlineMode)
    {
      return FakeData.getTransaction();
    }

    const path = `/v1/wallet-internal/payment/${paymentHash}`;
    const response = await Repository.client.get(path);
    return apiResponseToTransaction(response.data.data);
  }

  async getLatestRecipients(): Promise<LatestRecipient[]> {
    const path = `/v1/wallet-internal/recipients/latest`;
    const response = await Repository.client.get(path);
    return response.data.data.map((element: any) => {
      const latestRecipient = element as LatestRecipient;
      if(latestRecipient.internalUser)
        latestRecipient.internalUser = Pleb.fromJson(element.internalUser, '');
      return latestRecipient;
    }) as LatestRecipient[];
  }

  async markTransactionAsRead(paymentHash: string): Promise<void> {
    if(Repository.offlineMode)
    {
      return;
    }

    const path = `/v1/wallet-internal/payment/markAsRead`;
    await Repository.client.post(path, {
      paymentHash
    });
  } 

  async markTransactionAsAnnounced(paymentHash: string): Promise<void> {
    if(Repository.offlineMode)
    {
      return;
    }

    const path = `/v1/wallet-internal/payment/markAsAnnounced`;
    await Repository.client.post(path, {
      paymentHash
    });
  } 

  async scan(data: string): Promise<ScanData> {
    const path = 'v1/wallet-internal/scan';

    try {
      const response = await Repository.client.post(path, {
        data: data
      });
      return response.data.data as ScanData;
    } catch (err) {
      throw err;
    }
  }

  async payLnurl(data: string, amount: number, comment: string, userId?: string, anonymous?: boolean): Promise<Transaction> {
    const path = 'v1/wallet-internal/pay';
    const response = await Repository.client.post(path, {
      data: data,
      amount: amount,
      comment: comment,
      userId: userId,
      anonymous: anonymous
    });
    return response.data.data as Transaction;
  }

  async regenerateSecretNWC(): Promise<string> {
    const path = 'v1/wallet-internal/nwc/regenerate-connection-string';
    const response = await Repository.client.post(path);
    return response.data.data.nwcConnectionString as string;
  }

  async scanBulkPayment(type: string, id: string): Promise<BulkPayment> {
    const path = 'v1/wallet-internal/bulk/scan';
    const response = await Repository.client.post(path, {
      type: type,
      id: id,
    });
    return new BulkPayment(response.data.data);
  }

  async payBulkPayment(type: string, id: string, amount: number, comment: string, anonymous?: boolean): Promise<Transaction> {
    const path = 'v1/wallet-internal/bulk/pay';
    const response = await Repository.client.post(path, {
      type: type,
      id: id,
      amountMsat: amount * 1000,
      comment: comment,
      anonymous: anonymous
    });
    return response.data.data as Transaction;
  }

  async searchWallet(currentUserId: string, query: string, signal?: AbortSignal): Promise<SearchResponse[]> {
    const path = `/v1/wallet-internal/users/search`;
    const response = await Repository.client.post(path, {
      searchTerm: query,
      searchTypes: [TypeSearch.user, TypeSearch.bulk_special, TypeSearch.city, TypeSearch.distanceFromMyself]
    }, { signal });
    return response.data.data.map((element: any) => {
      switch(element.type) {
        case TypeSearch.user:
          return {
            type: TypeSearch.user,
            user: Pleb.fromJson(element, currentUserId)
          }
        case TypeSearch.bulk_special:
          return {
            type: TypeSearch.bulk_special,
            bulkSpecial: new BulkPayment({...element.bulkSpecial, type: BulkTypes.special}),
            imgUrl: element?.imgUrl ?? undefined
          }
        case TypeSearch.city:
          return {
            type: TypeSearch.city,
            city: element.city
          }
        case TypeSearch.distanceFromMyself:
          return {
            type: TypeSearch.distanceFromMyself,
            distanceFromMyself: element.distanceFromMyself
          }
      }
    }) as SearchResponse[];
  }
}

// Create a singleton instance
export const walletRepository = new WalletRepository();
